import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bookmark, Briefcase, MapPin, Clock, DollarSign, Building2, Trash2 } from "lucide-react";

const SavedOpportunities = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [savedOpportunities, setSavedOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSavedOpportunities();
  }, []);

  const fetchSavedOpportunities = async () => {
    try {
      const response = await axios.get(`${API}/saved-opportunities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedOpportunities(response.data);
    } catch (error) {
      console.error("Error loading saved opportunities:", error);
      toast.error("Failed to load saved opportunities");
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (opportunityId) => {
    try {
      await axios.delete(`${API}/opportunities/${opportunityId}/save`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Opportunity removed from saved");
      fetchSavedOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove opportunity");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Bookmark className="w-10 h-10 text-indigo-500" />
            Saved Opportunities
          </h1>
          <p className="text-gray-600 text-lg">
            {savedOpportunities.length} {savedOpportunities.length === 1 ? "opportunity" : "opportunities"} saved
          </p>
        </div>

        {/* Opportunities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {savedOpportunities.map((opportunity) => (
            <Card key={opportunity.id} className="card-hover">
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge>{opportunity.field}</Badge>
                      <Badge className={opportunity.work_mode === "remote" ? "bg-green-100 text-green-700" : opportunity.work_mode === "hybrid" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}>
                        {opportunity.work_mode}
                      </Badge>
                      <Badge className={opportunity.type === "internship" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"}>
                        {opportunity.type.toUpperCase()}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl mb-1">{opportunity.title}</CardTitle>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Building2 className="w-4 h-4" />
                      <span className="font-semibold">{opportunity.company_name}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleUnsave(opportunity.id)}
                  >
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600 line-clamp-2">{opportunity.description}</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>{opportunity.location}</span>
                  </div>
                  
                  {opportunity.type === "internship" && opportunity.stipend && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <DollarSign className="w-4 h-4" />
                      <span>Stipend: {opportunity.stipend}</span>
                    </div>
                  )}
                  
                  {opportunity.type === "job" && opportunity.salary && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <DollarSign className="w-4 h-4" />
                      <span>Salary: {opportunity.salary}</span>
                    </div>
                  )}

                  {opportunity.duration && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>Duration: {opportunity.duration}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-gray-600">
                    <Bookmark className="w-4 h-4" />
                    <span>Saved: {new Date(opportunity.saved_date).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="pt-4 border-t flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={() => navigate(`/opportunities/${opportunity.id}`)}
                  >
                    View Details
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleUnsave(opportunity.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {savedOpportunities.length === 0 && (
          <div className="text-center py-12">
            <Bookmark className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-4">You haven't saved any opportunities yet</p>
            <Button onClick={() => navigate("/opportunities")}>
              Browse Opportunities
            </Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SavedOpportunities;
